/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../assets/icons/activety-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as CardIcon } from '../../assets/icons/credit-card.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { CommercialSessionInterface } from '../../interface/charging-session.interface';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';

/**
 * Dimension entry inside a charging period
 */
export interface OcpiChargingPeriodDimension {
  type: string;
  volume: number;
}

/**
 * Charging period structure for OCPI 2.2.1
 */
export interface OcpiChargingPeriod {
  start_date_time: string;
  dimensions: OcpiChargingPeriodDimension[];
  tariff_id?: string;
}

/**
 * CDR Token structure
 */
export interface OcpiCdrToken {
  country_code: string;
  party_id: string;
  uid: string;
  type: string;
  contract_id: string;
}

/**
 * Cost structure
 */
export interface OcpiTotalCost {
  excl_vat?: number;
  incl_vat?: number;
}

/**
 * Commercial Session OCPI data structure
 */
export interface CommercialSessionData {
  country_code: string;
  party_id: string;
  id: string;
  start_date_time: string;
  end_date_time: string;
  kwh: number;
  cdr_token?: OcpiCdrToken;
  auth_method: string;
  location_id: string;
  evse_uid: string;
  connector_id: string;
  currency: string;
  charging_periods: OcpiChargingPeriod[];
  total_cost?: OcpiTotalCost;
  status: string;
  last_updated: string;
  [key: string]: any;
}

/**
 * Sample commercial session OCPI 2.2.1 data
 */
export const SAMPLE_COMMERCIAL_SESSION_DATA: CommercialSessionData = {
  country_code: 'BE',
  party_id: 'BEC',
  id: '101',
  start_date_time: '2015-06-29T22:39:09Z',
  end_date_time: '2015-06-29T23:50:16Z',
  kwh: 41.12,
  cdr_token: {
    country_code: 'NL',
    party_id: 'TST',
    uid: '123abc',
    type: 'RFID',
    contract_id: 'NL-TST-C12345678-S',
  },
  auth_method: 'WHITELIST',
  location_id: 'LOC1',
  evse_uid: '3256',
  connector_id: '1',
  currency: 'EUR',
  charging_periods: [
    {
      start_date_time: '2015-06-29T22:39:09Z',
      dimensions: [
        {
          type: 'ENERGY',
          volume: 120,
        },
        {
          type: 'MAX_CURRENT',
          volume: 30,
        },
      ],
    },
    {
      start_date_time: '2015-06-29T22:40:54Z',
      dimensions: [
        {
          type: 'ENERGY',
          volume: 41000,
        },
        {
          type: 'MIN_CURRENT',
          volume: 34,
        },
      ],
    },
    {
      start_date_time: '2015-06-29T23:07:09Z',
      dimensions: [
        {
          type: 'PARKING_TIME',
          volume: 0.718,
        },
      ],
      tariff_id: '12',
    },
  ],
  total_cost: {
    excl_vat: 8.5,
    incl_vat: 9.35,
  },
  status: 'COMPLETED',
  last_updated: '2015-06-29T23:50:17Z',
};

/**
 * Props for CommercialSessionSidePanel
 */
interface CommercialSessionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  selectedSession?: CommercialSessionInterface | any;
  sessions?: CommercialSessionInterface[];
}

/**
 * Formats ISO date string to readable "DD MMM YYYY, HH:MM AM/PM" format
 * @param {string | undefined | null} dateString - The ISO date string
 * @returns {string} The formatted date string
 */
const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const dateValue = ConvertUTCtoDate(dateString);
    const timeValue = FormatUtcString(ConvertUTCtoHHMM(dateString));
    if (dateValue && timeValue) {
      return `${dateValue}, ${timeValue}`;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return String(dateString);
};

/**
 * Calculates duration between start and end ISO timestamps in "Xh Ym Zs" format
 * @param {string} [startIso] - Start ISO timestamp
 * @param {string} [endIso] - End ISO timestamp
 * @param {number} [seconds] - Duration in seconds
 * @returns {string} Formatted duration string
 */
const formatDuration = (startIso?: string, endIso?: string, seconds?: number): string => {
  let totalSeconds = 0;
  if (seconds !== undefined && seconds !== null && seconds > 0) {
    totalSeconds = Math.floor(seconds);
  } else if (startIso && endIso) {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    const diffMs = Math.max(end - start, 0);
    totalSeconds = Math.floor(diffMs / 1000);
  }

  if (totalSeconds <= 0) return '—';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${String(hours).padStart(2, '0')}h`);
  if (minutes > 0 || hours > 0) parts.push(`${String(minutes).padStart(2, '0')}m`);
  parts.push(`${String(secs).padStart(2, '0')}s`);

  return parts.join(' ');
};

/**
 * Formats a charging period dimension type label for display with localization support
 * @param {string} type - Dimension type (e.g. ENERGY, POWER, STATE_OF_CHARGE)
 * @param {any} t - Translation function
 * @returns {string} Localized dimension type label
 */
const formatDimensionType = (type: string, t: any): string => {
  if (!type) return '';
  const upper = String(type).trim().toUpperCase();
  const fallbackMap: Record<string, string> = {
    ENERGY: 'Energy',
    POWER: 'Power',
    STATE_OF_CHARGE: 'State of Charge',
    SOC: 'State of Charge',
    PARKING_TIME: 'Parking Time',
    TIME: 'Time',
    MAX_CURRENT: 'Max Current',
    MIN_CURRENT: 'Min Current',
    CURRENT: 'Current',
    FLAT: 'Flat',
    RESERVATION_TIME: 'Reservation Time',
  };

  const defaultText = fallbackMap[upper] || upper.replace(/_/g, ' ');

  return String(
    t(`dim_${upper}`, {
      defaultValue: t(upper, {
        defaultValue: defaultText,
      }),
    }),
  );
};

/**
 * Formats charging period dimension volume with appropriate units
 * @param {string} type - Dimension type (e.g. ENERGY, MAX_CURRENT, MIN_CURRENT, PARKING_TIME)
 * @param {number} volume - Dimension numeric volume
 * @returns {string} Formatted volume string with units
 */
const formatDimensionValue = (type: string, volume: number): string => {
  if (volume === undefined || volume === null) return '—';
  const upperType = type?.toUpperCase() || '';
  if (upperType === 'ENERGY') {
    if (volume >= 1000) {
      return `${ToFixedValue(volume / 1000, 2)} kWh (${volume} Wh)`;
    }
    return `${volume} Wh`;
  }
  if (upperType === 'MAX_CURRENT' || upperType === 'MIN_CURRENT' || upperType === 'CURRENT') {
    return `${volume} A`;
  }
  if (upperType === 'POWER') {
    return `${volume} kW`;
  }
  if (upperType === 'PARKING_TIME' || upperType === 'TIME') {
    const mins = Math.round(volume * 60);
    return `${volume} hrs (${mins} min)`;
  }
  if (upperType === 'STATE_OF_CHARGE' || upperType === 'SOC') {
    return String(volume).includes('%') ? `${volume}` : `${volume}%`;
  }
  return `${volume}`;
};

/**
 * Resolves CSS class modifier for a given session status
 * @param {string} [status] - The session status
 * @returns {string} CSS class modifier name
 */
const getStatusClass = (status?: string): string => {
  if (!status) return 'default';
  const lower = status.toLowerCase();
  if (lower === 'completed' || lower === 'finished') return 'completed';
  if (
    lower === 'active' ||
    lower === 'inprogress' ||
    lower === 'in_progress' ||
    lower === 'in progress'
  ) {
    return 'in-progress';
  }
  if (lower === 'pending') return 'pending';
  if (lower === 'invalid' || lower === 'failed' || lower === 'terminated') return 'failed';
  return 'default';
};

/**
 * Normalizes or dynamically derives charging periods from a session row
 * @param {any} session - The session row data
 * @param {number} energyKwh - The energy in kWh
 * @param {string} startDateTime - The start date time string
 * @param {string} endDateTime - The end date time string
 * @returns {OcpiChargingPeriod[]} Normalized list of charging periods
 */
const resolveChargingPeriods = (
  session: any,
  energyKwh: number,
  startDateTime: string,
  endDateTime: string,
): OcpiChargingPeriod[] => {
  if (!session) {
    return SAMPLE_COMMERCIAL_SESSION_DATA.charging_periods;
  }

  const rawPeriods =
    session.charging_periods ||
    session.chargingPeriods ||
    session.periods ||
    session.cdrDetails?.charging_periods ||
    session.cdrDetails?.chargingPeriods;

  if (Array.isArray(rawPeriods) && rawPeriods.length > 0) {
    return rawPeriods.map((period: any) => {
      const pStart =
        period.start_date_time ||
        period.startDateTime ||
        period.startTime ||
        period.startDate ||
        startDateTime;
      const pTariff =
        period.tariff_id ||
        period.tariffId ||
        (typeof period.tariff === 'string' ? period.tariff : period.tariff?.tariffId);

      const rawDimensions = period.dimensions || [];
      const dimensions: OcpiChargingPeriodDimension[] = Array.isArray(rawDimensions)
        ? rawDimensions.map((dim: any) => ({
            type: String(dim.type || dim.dimensionType || 'ENERGY').toUpperCase(),
            volume: Number(dim.volume ?? dim.value ?? 0),
          }))
        : [];

      return {
        start_date_time: pStart,
        dimensions,
        tariff_id: pTariff,
      };
    });
  }

  // Dynamically derive period(s) from session's own data
  const tariffId =
    session.tariff?.tariffId ||
    session.tariff?._id ||
    session.tariff?.name ||
    (typeof session.tariff === 'string' ? session.tariff : undefined) ||
    session.tariffId;

  const durationSec =
    session.durationSeconds !== undefined && session.durationSeconds !== null
      ? Number(session.durationSeconds)
      : undefined;

  let durationHours = 0;
  if (durationSec !== undefined) {
    durationHours = Number((durationSec / 3600).toFixed(3));
  } else if (startDateTime && endDateTime) {
    const diffMs = Math.max(new Date(endDateTime).getTime() - new Date(startDateTime).getTime(), 0);
    durationHours = Number((diffMs / (1000 * 3600)).toFixed(3));
  }

  const energyWh = Number((energyKwh * 1000).toFixed(0));

  const dimensions: OcpiChargingPeriodDimension[] = [];

  if (energyKwh > 0) {
    dimensions.push({
      type: 'ENERGY',
      volume: energyWh,
    });
  }

  if (durationHours > 0) {
    dimensions.push({
      type: 'PARKING_TIME',
      volume: durationHours,
    });
  }

  if (session.maxCurrent || session.current) {
    dimensions.push({
      type: 'MAX_CURRENT',
      volume: Number(session.maxCurrent || session.current),
    });
  }

  if (session.minCurrent) {
    dimensions.push({
      type: 'MIN_CURRENT',
      volume: Number(session.minCurrent),
    });
  }

  if (dimensions.length === 0) {
    dimensions.push({
      type: 'ENERGY',
      volume: energyWh || 0,
    });
  }

  return [
    {
      start_date_time: startDateTime,
      dimensions,
      tariff_id: tariffId,
    },
  ];
};

/**
 * CommercialSessionSidePanel component
 * Displays detailed information about a commercial charging session (OCPI 2.2.1 format)
 * @param {CommercialSessionSidePanelProperties} properties - Component properties
 * @returns {React.JSX.Element} The rendered side panel component
 */
function CommercialSessionSidePanel(
  properties: CommercialSessionSidePanelProperties,
): React.JSX.Element {
  const { t } = useTranslation(['sidePanel', 'transaction']);
  const { fetchIdFrom, id, selectedSession, sessions = [] } = properties;
  const [searchParameters] = useSearchParams();

  // Determine active session ID
  const sessionId = useMemo(() => {
    if (fetchIdFrom === 'queryParams') {
      return searchParameters.get('sessionId') || searchParameters.get('id') || id;
    }
    return id;
  }, [fetchIdFrom, searchParameters, id]);

  // Find matching session row if available
  const matchedSession = useMemo(() => {
    if (selectedSession) return selectedSession;
    if (!sessionId || sessions.length === 0) return null;
    return sessions.find(
      (sessionItem: any) =>
        String(sessionItem.id) === String(sessionId) ||
        String(sessionItem._id) === String(sessionId) ||
        String(sessionItem.transactionId) === String(sessionId),
    );
  }, [selectedSession, sessionId, sessions]);

  // Build full OCPI session data, seamlessly merging row details with sample OCPI structure
  const sessionData: CommercialSessionData = useMemo(() => {
    if (!matchedSession) {
      return SAMPLE_COMMERCIAL_SESSION_DATA;
    }

    const startDateTime =
      matchedSession.transactionStartsAt ||
      matchedSession.startDate ||
      matchedSession.start_date_time ||
      matchedSession.createdAt ||
      SAMPLE_COMMERCIAL_SESSION_DATA.start_date_time;

    const endDateTime =
      matchedSession.transactionEndsAt ||
      matchedSession.endDate ||
      matchedSession.end_date_time ||
      matchedSession.updatedAt ||
      SAMPLE_COMMERCIAL_SESSION_DATA.end_date_time;

    let energyKwh = SAMPLE_COMMERCIAL_SESSION_DATA.kwh;
    if (
      matchedSession.powerConsumedInKwh !== undefined &&
      matchedSession.powerConsumedInKwh !== null
    ) {
      energyKwh = Number(matchedSession.powerConsumedInKwh);
    } else if (matchedSession.totalEnergy !== undefined && matchedSession.totalEnergy !== null) {
      energyKwh = Number(matchedSession.totalEnergy);
    }

    const currency = matchedSession.currency || SAMPLE_COMMERCIAL_SESSION_DATA.currency;

    let totalAmount: number | undefined;
    if (matchedSession.totalAmount !== undefined && matchedSession.totalAmount !== null) {
      totalAmount = Number(matchedSession.totalAmount);
    } else if (matchedSession.amount !== undefined && matchedSession.amount !== null) {
      totalAmount = Number(matchedSession.amount);
    }

    const totalCost: OcpiTotalCost =
      totalAmount !== undefined
        ? {
            excl_vat: Number((totalAmount * 0.85).toFixed(2)),
            incl_vat: Number(totalAmount.toFixed(2)),
          }
        : matchedSession.total_cost ||
          matchedSession.totalCost ||
          SAMPLE_COMMERCIAL_SESSION_DATA.total_cost;

    const statusValue = (
      matchedSession.status || SAMPLE_COMMERCIAL_SESSION_DATA.status
    ).toUpperCase();

    const cdrToken: OcpiCdrToken = matchedSession.cdr_token ||
      matchedSession.cdrToken || {
        country_code:
          matchedSession.tokenCountryCode ||
          SAMPLE_COMMERCIAL_SESSION_DATA.cdr_token?.country_code ||
          'NL',
        party_id:
          matchedSession.tokenPartyId ||
          SAMPLE_COMMERCIAL_SESSION_DATA.cdr_token?.party_id ||
          'TST',
        uid:
          matchedSession.rfidNo ||
          matchedSession.tokenUid ||
          matchedSession.uid ||
          SAMPLE_COMMERCIAL_SESSION_DATA.cdr_token?.uid ||
          '123abc',
        type: matchedSession.tokenType || 'RFID',
        contract_id:
          matchedSession.userCustomId ||
          matchedSession.contractId ||
          SAMPLE_COMMERCIAL_SESSION_DATA.cdr_token?.contract_id ||
          'NL-TST-C12345678-S',
      };

    const chargingPeriods = resolveChargingPeriods(
      matchedSession,
      energyKwh,
      startDateTime,
      endDateTime,
    );

    return {
      country_code:
        matchedSession.country_code ||
        matchedSession.countryCode ||
        SAMPLE_COMMERCIAL_SESSION_DATA.country_code,
      party_id:
        matchedSession.party_id ||
        matchedSession.partyId ||
        SAMPLE_COMMERCIAL_SESSION_DATA.party_id,
      id: String(
        matchedSession.transactionId ||
          matchedSession.id ||
          matchedSession._id ||
          sessionId ||
          SAMPLE_COMMERCIAL_SESSION_DATA.id,
      ),
      start_date_time: startDateTime,
      end_date_time: endDateTime,
      kwh: energyKwh,
      cdr_token: cdrToken,
      auth_method:
        matchedSession.auth_method ||
        matchedSession.authMethod ||
        SAMPLE_COMMERCIAL_SESSION_DATA.auth_method,
      location_id:
        matchedSession.chargingStationName ||
        (typeof matchedSession.chargingStation === 'object'
          ? matchedSession.chargingStation?.name
          : matchedSession.chargingStation) ||
        matchedSession.locationId ||
        matchedSession.location_id ||
        SAMPLE_COMMERCIAL_SESSION_DATA.location_id,
      evse_uid:
        matchedSession.chargingPointSn ||
        matchedSession.chargingPointName ||
        matchedSession.evseUid ||
        matchedSession.evse_uid ||
        SAMPLE_COMMERCIAL_SESSION_DATA.evse_uid,
      connector_id:
        String(
          matchedSession.connectorId ||
            matchedSession.connector_id ||
            matchedSession.connector ||
            '',
        ) || SAMPLE_COMMERCIAL_SESSION_DATA.connector_id,
      currency,
      charging_periods: chargingPeriods,
      total_cost: totalCost,
      status: statusValue,
      last_updated:
        matchedSession.updatedAt ||
        matchedSession.last_updated ||
        matchedSession.createdAt ||
        SAMPLE_COMMERCIAL_SESSION_DATA.last_updated,
    };
  }, [matchedSession, sessionId]);

  const durationText = formatDuration(
    sessionData.start_date_time,
    sessionData.end_date_time,
    matchedSession?.durationSeconds,
  );

  const formattedEnergy = `${ToFixedValue(sessionData.kwh, 2)} kWh`;
  const totalInclVat = sessionData.total_cost?.incl_vat;
  const totalExclVat = sessionData.total_cost?.excl_vat;
  const currencyCode = sessionData.currency || 'EUR';

  /**
   * Resolves display text for cost
   * @returns {string} Formatted cost string
   */
  const getFormattedCost = (): string => {
    if (totalInclVat !== undefined) {
      return `${ToFixedValue(totalInclVat, 2)} ${currencyCode}`;
    }
    if (totalExclVat !== undefined) {
      return `${ToFixedValue(totalExclVat, 2)} ${currencyCode}`;
    }
    return '—';
  };
  const formattedCost = getFormattedCost();

  const statusClass = getStatusClass(sessionData.status);

  // Session info items
  const sessionInfoItems = [
    { label: t('sessionId', { defaultValue: 'Session ID' }), value: sessionData.sessionId },
    { label: t('ocppId', { defaultValue: 'OCPP ID' }), value: sessionData.id },
    { label: t('authMethod', { defaultValue: 'Auth Method' }), value: sessionData.auth_method },
    { label: t('currency', { defaultValue: 'Currency' }), value: sessionData.currency },
    {
      label: t('status', { defaultValue: 'Status' }),
      value: sessionData.status,
      isBadge: true,
    },
    {
      label: t('startTime', { defaultValue: 'Start Time' }),
      value: formatDateTime(sessionData.start_date_time),
    },
    {
      label: t('endTime', { defaultValue: 'End Time' }),
      value: formatDateTime(sessionData.end_date_time),
    },
    {
      label: t('lastUpdated', { defaultValue: 'Last Updated' }),
      value: formatDateTime(sessionData.last_updated),
    },
  ];

  // Location & EVSE items
  const locationItems = [
    { label: t('locationId', { defaultValue: 'Location ID' }), value: sessionData.location_id },
    { label: t('evseUid', { defaultValue: 'EVSE UID' }), value: sessionData.evse_uid },
    { label: t('connectorId', { defaultValue: 'Connector ID' }), value: sessionData.connector_id },
  ];

  return (
    <div className="commercial-session-side-panel-wrap">
      {/* Scrollable Panel Body */}
      <div className="panel-scrollable-content">
        {/* Top 4 Metrics Summary Cards */}
        <div className="metrics-summary-row">
          {/* Card 1: Status */}
          <div className="metric-card" title={sessionData.status}>
            <div className={`badge-icon-wrap status ${statusClass}`}>
              <ActivityIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('status', { defaultValue: 'STATUS' })}</span>
              <span className={`card-value status-${statusClass}`} title={sessionData.status}>
                {sessionData.status}
              </span>
            </div>
          </div>

          {/* Card 2: Duration */}
          <div className="metric-card" title={durationText}>
            <div className="badge-icon-wrap duration">
              <ClockIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('duration', { defaultValue: 'DURATION' })}</span>
              <span className="card-value" title={durationText}>
                {durationText}
              </span>
            </div>
          </div>

          {/* Card 3: Energy */}
          <div className="metric-card" title={formattedEnergy}>
            <div className="badge-icon-wrap energy">
              <LightningIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('energy', { defaultValue: 'ENERGY' })}</span>
              <span className="card-value" title={formattedEnergy}>
                {formattedEnergy}
              </span>
            </div>
          </div>

          {/* Card 4: Total Cost */}
          <div className="metric-card cost-card" title={formattedCost}>
            <div className="badge-icon-wrap cost">
              <CardIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('totalCost', { defaultValue: 'TOTAL COST' })}</span>
              <span className="card-value" title={formattedCost}>
                {formattedCost}
              </span>
            </div>
          </div>
        </div>

        {/* Section 1: Session Information */}
        <div className="detail-section-card">
          <div className="card-header session-info">
            <CalendarIcon />
            <h3>{t('sessionInformation', { defaultValue: 'Session Information' })}</h3>
          </div>
          <div className="card-body-grid">
            {sessionInfoItems.map((item, index) => (
              <div className="grid-item" key={index}>
                <span className="item-label">{item.label}</span>
                {item.isBadge ? (
                  <span className={`status-pill ${statusClass}`}>{item.value}</span>
                ) : (
                  <span className="item-value">{item.value || '—'}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Section 2: Location & EVSE */}
        <div className="detail-section-card">
          <div className="card-header location-info">
            <LocationIcon />
            <h3>{t('locationAndEvse', { defaultValue: 'Location & EVSE' })}</h3>
          </div>
          <div className="card-body-grid">
            {locationItems.map((item, index) => (
              <div className="grid-item" key={index}>
                <span className="item-label">{item.label}</span>
                <span className="item-value">{item.value || '—'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 5: Charging Periods */}
        <div className="detail-section-card charging-periods-section">
          <div className="card-header periods-info">
            <ActivityIcon />
            <h3>
              {t('chargingPeriodsTitle', {
                defaultValue: 'Charging Periods ({{count}})',
                count: sessionData.charging_periods?.length || 0,
              })}
            </h3>
          </div>
          {sessionData.charging_periods && sessionData.charging_periods.length > 0 ? (
            <div className="periods-list">
              {sessionData.charging_periods.map((period, periodIndex) => (
                <div className="period-card" key={periodIndex}>
                  <div className="period-card-header">
                    <div className="period-title-wrap">
                      <span className="period-index-badge">#{periodIndex + 1}</span>
                      <span className="period-time">{formatDateTime(period.start_date_time)}</span>
                    </div>
                  </div>

                  <div className="period-dimensions-wrap">
                    {period.tariff_id && (
                      <span className="period-tariff-badge">
                        {t('tariffId', { defaultValue: 'Tariff' })}: {period.tariff_id}
                      </span>
                    )}
                    <span className="dimensions-title">
                      {t('dimensions', { defaultValue: 'Dimensions' })}
                    </span>
                    <div className="dimensions-grid">
                      {period.dimensions?.map((dim, dimIndex) => (
                        <div className="dimension-chip" key={dimIndex}>
                          <span className="dim-type">{formatDimensionType(dim.type, t)}</span>
                          <span className="dim-volume">
                            {formatDimensionValue(dim.type, dim.volume)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-periods-text">
              {t('noChargingPeriods', {
                defaultValue: 'No charging periods recorded for this session.',
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

CommercialSessionSidePanel.defaultProps = {
  id: undefined,
  selectedSession: undefined,
  sessions: [],
};

export default CommercialSessionSidePanel;
